import { useRouter } from "expo-router"
import React from "react"
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Platform, View } from "react-native"

import { ENVIRONMENT } from "@/base/environment"
import { createFarmAsync } from "@/base/farmhq-api"
import { loadActiveFarmAsync } from "@/base/load-app"
import { logger } from "@/base/logger"
import countries from "@/constants/country-region.json"
import { useErrorHandler } from "@/hooks/useErrorHandler"
import { useFormValidation } from "@/hooks/useFormvalidation"
import { useIsPending } from "@/redux/requests.reducer"
import { geocodeFarmAddressAsync } from "@/redux/user-farms.reducer"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { Box, Column, FormControl, Row, SPACING } from "@/theme"

import { ActionButtons } from "./ActionButtons"
import { DataCollectionNoticeDialog } from "./DataCollectionNoticeDialog"

import type { FarmAccountProperties } from "@/redux/models"

import type { AcceptsChildren } from "@/theme"

import { StyleSheet } from "react-native"

const PRIORITY_COUNTRIES = ["US", "CA", "MX", "IT"]

const COUNTRIES_SORTED = [...countries]
  .sort((a, b): -1 | 0 | 1 => {
    if (a.slug === b.slug) {
      return 0
    }
    if (PRIORITY_COUNTRIES.includes(a.slug.toLocaleUpperCase())) {
      return -1
    }
    return 1
  })
  .map(({ name, regions, slug }) => ({
    key: name,
    label: name,
    regions,
    value: slug,
  }))

interface ProviderProps {
  defaultValues?: Partial<FarmAccountProperties>
  onCancel: (() => void) | undefined
}

/**
 *
 */
function useCreateFarm({ defaultValues, onCancel }: ProviderProps) {
  const dispatch = useRootDispatch()
  const handleError = useErrorHandler()
  const [isCreatingFarm, setIsCreatingFarm] = React.useState(false)

  const isLoading = useIsPending("CreateFarm", "GeocodeFarmAddress")

  const form = useForm<FarmAccountProperties>({
    defaultValues: {
      address_line_1: "",
      address_line_2: "",
      city: "",
      country: "US",
      name: "",
      postalCode: "",
      region: "WA",
      ...defaultValues,
    },
  })
  const router = useRouter()
  const onSubmit = form.handleSubmit(async (values) => {
    try {
      // Extra check to prevent duplicate submissions
      setIsCreatingFarm(true)
      if (!isCreatingFarm && !isLoading) {
        const farmWithGeocodedLocation = await dispatch(
          geocodeFarmAddressAsync(values),
        ).unwrap()
        const gpsLocation = farmWithGeocodedLocation.gpsLocation ?? null

        const {
          farm: { id: activeFarmId },
        } = await dispatch(
          createFarmAsync({ ...values, gpsLocation }),
        ).unwrap()

        await dispatch(loadActiveFarmAsync({ activeFarmId }))
        router.navigate("/")
        return activeFarmId
      }
    } catch (error) {
      handleError(error, { toastMessage: "default" })
    } finally {
      setIsCreatingFarm(false)
    }
    return undefined
  })

  return {
    form,
    isLoading: isLoading || isCreatingFarm,
    onCancel,
    onSubmit,
  }
}

type ContextValue = ReturnType<typeof useCreateFarm>

const Context = React.createContext<ContextValue | undefined>(undefined)
export type FormValues = FarmAccountProperties

/**
 *
 */
function CreateFarmContextProvider({
  children,
  defaultValues,
  ...rest
}: AcceptsChildren & ProviderProps): React.JSX.Element | null {
  const value = useCreateFarm({ ...rest, defaultValues })
  return (
    <Context.Provider value={value}>
      <FormProvider {...value.form}>{children}</FormProvider>
    </Context.Provider>
  )
}

function useContext(): ContextValue {
  const ctx = React.useContext(Context)
  if (typeof ctx === "undefined") {
    throw new TypeError(
      `CreateFarmForm Context must be used inside of provider`,
    )
  }
  return ctx
}

/**
 *
 */
function FarmNameField(): React.JSX.Element {
  const { required } = useFormValidation()
  const { t } = useTranslation("noFarms")

  const { control } = useFormContext<FarmAccountProperties>()
  return (
    <Controller
      control={control}
      name={"name"}
      rules={{ required }}
      render={({
        field: { onChange, ...field },
        fieldState,
      }): React.JSX.Element => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)

        return (
          <FormControl.Provider id={"name"} isInvalid={isInvalid}>
            <FormControl.Label>{t("createFarmName.label")}</FormControl.Label>
            <FormControl.Input
              placeholder={t("createFarmName.placeholder")}
              textContentType={"organizationName"}
              onChangeText={onChange}
              {...field}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
    />
  )
}

/**
 *
 */
function AddressLineOneField(): React.JSX.Element {
  const { control } = useFormContext<FarmAccountProperties>()
  const { t } = useTranslation("noFarms")

  const { required } = useFormValidation()
  const labelText = t("createFarmAddress_line_1.label")
  const placeholderText = t("createFarmAddress_line_1.placeholder")

  return (
    <Controller
      control={control}
      name={"address_line_1"}
      rules={{ required }}
      render={({
        field: { onChange, ...field },
        fieldState,
      }): React.JSX.Element => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)

        return (
          <FormControl.Provider id={"address_line_1"} isInvalid={isInvalid}>
            <FormControl.Label>{labelText}</FormControl.Label>
            <FormControl.Input
              {...field}
              autoComplete={"postal-address-region"}
              placeholder={placeholderText}
              textContentType={"streetAddressLine1"}
              onChangeText={onChange}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
    />
  )
}

/**
 *
 */
function AddressLineTwoField(): React.JSX.Element {
  const { control } = useFormContext<FarmAccountProperties>()
  const { t } = useTranslation("noFarms")

  const labelText = t("createFarmAddress_line_2.label")
  const placeholderText = t("createFarmAddress_line_2.placeholder")
  return (
    <Controller
      control={control}
      name={"address_line_2"}
      render={({
        field: { onChange, value, ...field },
        fieldState,
      }): React.JSX.Element => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)

        return (
          <FormControl.Provider id={"address_line_2"} isInvalid={isInvalid}>
            <FormControl.Label>{labelText}</FormControl.Label>
            <FormControl.Input
              {...field}
              placeholder={placeholderText}
              textContentType={"streetAddressLine2"}
              value={value ?? ""}
              onChangeText={onChange}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
    />
  )
}

/**
 *
 */
function CityField(): React.JSX.Element | null {
  const { control } = useFormContext<FarmAccountProperties>()
  const { t } = useTranslation("noFarms")

  const { required } = useFormValidation()

  const placeholderText = t("createFarmCity.placeholder")
  const labelText = t("createFarmCity.label")
  return (
    <Controller
      control={control}
      name={"city"}
      rules={{ required }}
      render={({
        field: { onChange, ...field },
        fieldState,
      }): React.JSX.Element => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)

        return (
          <FormControl.Provider id={"city"} isInvalid={isInvalid}>
            <FormControl.Label>{labelText}</FormControl.Label>
            <FormControl.Input
              {...field}
              placeholder={placeholderText}
              textContentType={"addressCity"}
              onChangeText={onChange}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
    />
  )
}

/**
 *
 */
function PostalCodeField(): React.JSX.Element | null {
  const { control } = useFormContext<FarmAccountProperties>()
  const { required } = useFormValidation()
  const { t } = useTranslation("noFarms")

  const labelText = t("createFarmPostalCode.label")
  const placeholderText = t("createFarmPostalCode.placeholder")
  return (
    <Controller
      control={control}
      name={"postalCode"}
      rules={{ required }}
      render={({
        field: { onChange, ...field },
        fieldState,
      }): React.JSX.Element => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)
        return (
          <FormControl.Provider id={"postalCode"} isInvalid={isInvalid}>
            <FormControl.Label>{labelText}</FormControl.Label>
            <FormControl.Input
              {...field}
              placeholder={placeholderText}
              textContentType={"postalCode"}
              onChangeText={onChange}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
    />
  )
}

/**
 *
 */
function CountrySelectField(): React.JSX.Element | null {
  const { control } = useFormContext<FarmAccountProperties>()
  const { t } = useTranslation("noFarms")

  const { required } = useFormValidation()

  const labelText = t("createFarmCountry.label")
  const placeholderText = t("createFarmCountry.placeholder")
  return (
    <Controller
      control={control}
      name={"country"}
      rules={{ required }}
      render={({
        field: { onChange, value },
        fieldState,
      }): React.JSX.Element => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)

        return (
          <FormControl.Provider id={"country"} isInvalid={isInvalid}>
            <FormControl.Label>{labelText}</FormControl.Label>
            <FormControl.Select
              label={labelText}
              options={COUNTRIES_SORTED}
              placeholder={placeholderText}
              selectedValue={value}
              onValueChange={onChange}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
    />
  )
}

/**
 * TODO: Make a map of these
 */
function RegionSelectField(): React.JSX.Element | null {
  const { control, setValue, watch } = useFormContext<FarmAccountProperties>()
  const countryValue = watch("country")
  const { t } = useTranslation("noFarms")
  const { required } = useFormValidation()

  const options = React.useMemo(() => {
    const regions: Array<{ label: string; value: string }> = []
    const selectedCountry = COUNTRIES_SORTED.find((country) => {
      return country.value === countryValue
    })
    if (selectedCountry) {
      for (const [regionName, regionSlug] of selectedCountry.regions) {
        if (typeof regionName === "string" && typeof regionSlug === "string") {
          regions.push({ label: regionName, value: regionSlug })
        }
      }
    }
    return regions
  }, [countryValue])
  const placeholderText = t("createFarmRegion.placeholder")
  const labelText = t("createFarmRegion.label")
  return (
    <Controller
      control={control}
      name={"region"}
      rules={{ required }}
      render={({
        field: { onChange, value },
        fieldState,
      }): React.JSX.Element => {
        const errorMessage = fieldState.error?.message
        const isInvalid = Boolean(errorMessage)

        return (
          <FormControl.Provider
            id={"region"}
            isDisabled={!countryValue}
            isInvalid={isInvalid}
          >
            <FormControl.Label>{labelText}</FormControl.Label>
            <FormControl.Select
              label={labelText}
              options={options}
              placeholder={placeholderText}
              selectedValue={value}
              onValueChange={(nextValue): void => {
                if (nextValue !== countryValue) {
                  setValue("region", "")
                }
                return onChange(nextValue)
              }}
            />
            <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
          </FormControl.Provider>
        )
      }}
    />
  )
}

/**
 *
 */
function FormFields(): React.JSX.Element {
  const { isLoading, onCancel, onSubmit } = useContext()
  const { t } = useTranslation("noFarms")

  return (
    <Column space={"$1"}>
      <FarmNameField />
      <AddressLineOneField />
      <AddressLineTwoField />
      <Row>
        <Box flexGrow={1} mr={"$4"}>
          <CityField />
        </Box>
        <Box flexGrow={1}>
          <PostalCodeField />
        </Box>
      </Row>
      <Row>
        <View style={styles.countrySelectContainer}>
          <CountrySelectField />
        </View>
        <View style={styles.regionSelectContainer}>
          <RegionSelectField />
        </View>
      </Row>
      <ActionButtons
        isDisabled={isLoading}
        isLoading={isLoading}
        my={"$4"}
        orientation={"horizontal"}
        onPressCancel={onCancel}
        onPressSubmit={onSubmit}
      />
      <DataCollectionNoticeDialog
        bodyText={t("whatWeUseFarmLocationForText")}
        titleText={t("whatWeUseFarmLocationForTitle")}
      />
    </Column>
  )
}

let DEFAULT_VALUES_DEV_ONLY: Partial<FormValues> | undefined
if (__DEV__) {
  const useDefaultsInWeb = Platform.OS === "web" && !ENVIRONMENT.isEndToEndTest
  if (useDefaultsInWeb || Platform.OS === "ios" || Platform.OS === "android") {
    try {
      DEFAULT_VALUES_DEV_ONLY = JSON.parse(
        process.env.EXPO_PUBLIC_DEFAULT_FARM_ADDRESS ?? "{}",
      ) as Partial<FormValues>
    } catch (error) {
      logger.warn("Failed to parse EXPO_PUBLIC_DEFAULT_FARM_ADDRESS")
      logger.warn(error)
    }
  }
}

/**
 *
 */
export function CreateFarmForm(props: ProviderProps) {
  return (
    <CreateFarmContextProvider
      defaultValues={DEFAULT_VALUES_DEV_ONLY}
      {...props}
    >
      <FormFields />
    </CreateFarmContextProvider>
  )
}
const styles = StyleSheet.create({
  countrySelectContainer: {
    flex: 1,
    marginRight: SPACING.$4,
    minWidth: 150,
    // width: "50%",
  },
  regionSelectContainer: {
    flex: 1,
    minWidth: 150,
    // width: "50%",
  },
})
